/**
 * G11 announcements ribbon for the dashboard home. Ported from
 * `frontend/src/components/dashboard/announcements-ribbon.tsx`.
 *
 * Renders ONLY pinned announcements (max 3) as stacked indigo ribbons
 * directly under the welcome header. Non-pinned items are summarised
 * as a "View all (N)" link; the full list lives on a future
 * /dashboard/announcements page.
 *
 * Per-user dismissal is persisted in localStorage under
 * `workright.announcement.dismissed.{id}` - purely a UI preference
 * (no auth state, no token). A dismissal sticks until the user clears
 * their browser data; the server keeps the announcement live.
 *
 * Loading -> one-line skeleton.
 * Error   -> silent (announcements are decorative).
 *
 * Note: `/dashboard/announcements` is not yet a TanStack-Router route
 * in the SPA; the View-all CTA is widened through a `string`-typed
 * variable so the typed `to=` check stays loose (same pattern as
 * `dashboard.analytics.tsx`). Once the page lands the literal can be
 * tightened.
 */

import { useState } from "react";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Pin, X } from "lucide-react";
import { announcementsApi } from "@/lib/api";
import { useFeatures } from "@/lib/auth";
import type { Announcement } from "@/lib/api/announcements";
import { Skeleton } from "@/components/ui/skeleton";
import { relativeTime } from "@/lib/relative-time";

const MAX_PINNED = 3;
const BODY_TRUNCATE = 200;

/**
 * Shared query definition — the single source for this query's key + fn.
 * The Me-home route loader (app.$tenantSlug.index.tsx) prefetches with
 * the SAME options so hover-preload warms exactly this cache entry (A9).
 */
export const announcementsQueryOptions = queryOptions({
  queryKey: ["announcements", "list"] as const,
  queryFn: ({ signal }) => announcementsApi.listAnnouncements(signal),
  staleTime: 60 * 1000,
  retry: false,
});

function dismissKey(id: number): string {
  return `workright.announcement.dismissed.${id}`;
}

function isDismissed(id: number): boolean {
  if (typeof globalThis.window === "undefined") return false;
  try {
    return globalThis.window.localStorage.getItem(dismissKey(id)) === "1";
  } catch {
    return false;
  }
}

function markDismissed(id: number): void {
  if (typeof globalThis.window === "undefined") return;
  try {
    globalThis.window.localStorage.setItem(dismissKey(id), "1");
  } catch {
    /* non-fatal */
  }
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + "…";
}

function Ribbon({
  announcement,
  onDismiss,
}: {
  announcement: Announcement;
  onDismiss: (id: number) => void;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-primary/20 bg-accent px-4 py-3 text-accent-foreground">
      <span className="mt-0.5 shrink-0">
        <Pin className="h-4 w-4 text-primary" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <p className="text-sm font-semibold">{announcement.title}</p>
          <span className="text-xs text-muted-foreground">
            {relativeTime(announcement.posted_at)}
          </span>
        </div>
        <p className="mt-0.5 text-sm text-accent-foreground/90">
          {truncate(announcement.body, BODY_TRUNCATE)}
        </p>
      </div>
      <button
        type="button"
        onClick={() => onDismiss(announcement.id)}
        aria-label={`Dismiss announcement: ${announcement.title}`}
        className="shrink-0 rounded p-1 opacity-70 transition-opacity hover:opacity-100 pointer-coarse:-m-2.5 pointer-coarse:p-3.5"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// String-widened path: TanStack Router's typed `to=` rejects unknown
// route literals at typecheck time. Until /dashboard/announcements
// has a real route, we widen to `string` so the Link still routes
// (matching the precedent in dashboard.analytics.tsx).
const ANNOUNCEMENTS_PATH: string = "/app/$tenantSlug/announcements";

export function AnnouncementsRibbon() {
  // POSH only (migration 162): announcements are not on the plan.
  const platform = useFeatures().has("hr_platform");
  const query = useQuery({ ...announcementsQueryOptions, enabled: platform });

  // We track dismissed ids in component state so the ribbon visibly
  // disappears on click without a refetch. The localStorage write is
  // the persistent source of truth across reloads.
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());

  const onDismiss = (id: number) => {
    markDismissed(id);
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  if (!platform || query.isError) return null;

  if (query.isPending) {
    return <Skeleton className="mb-4 h-10 w-full" />;
  }

  const items = query.data?.items ?? [];
  if (items.length === 0) return null;

  const liveItems = items.filter(
    (a) => !dismissedIds.has(a.id) && !isDismissed(a.id),
  );
  const pinned = liveItems
    .filter((a) => a.is_pinned)
    .slice(0, MAX_PINNED);
  const otherCount = liveItems.length - pinned.length;

  if (pinned.length === 0 && otherCount <= 0) return null;

  return (
    <div className="mb-4 flex flex-col gap-2">
      {pinned.map((a) => (
        <Ribbon key={a.id} announcement={a} onDismiss={onDismiss} />
      ))}
      {otherCount > 0 && (
        <Link
          to={ANNOUNCEMENTS_PATH}
          className="relative self-end text-xs text-primary hover:underline pointer-coarse:after:absolute pointer-coarse:after:-inset-x-2 pointer-coarse:after:-inset-y-3.5"
        >
          View all ({otherCount})
        </Link>
      )}
    </div>
  );
}
